/**
 * Rich answer blocks: visuals Elio can attach to a message (destination cards, a side-by-side
 * comparison, a list of places, a route). They only display information; nothing in a block changes
 * the traveller's data. Shared by the Worker (validates what the model sends) and the app (renders).
 */
import { z } from 'zod';

const text = (n: number) => z.string().trim().min(1).max(n);
const optText = (n: number) => z.string().trim().max(n).nullish().transform((v) => (v ? v : null));
const lat = z.number().min(-90).max(90);
const lon = z.number().min(-180).max(180);
const optUrl = z.string().trim().max(500).regex(/^https:\/\//, 'Use an https link').nullish().transform((v) => v ?? null);

export const LEG_MODES = ['train', 'car', 'bus', 'flight', 'ferry', 'walk', 'bike'] as const;

export const destinationsBlock = z.object({
  type: z.literal('destinations'),
  title: optText(120),
  items: z.array(z.object({
    name: text(80),
    country: optText(60),
    summary: text(280).describe('Why it suits this traveller, in one or two sentences.'),
    highlights: z.array(text(40)).max(4).default([]),
    best_time: optText(60),
    budget: optText(60).describe('Rough cost level, e.g. "€€ · about €140 a day for two".'),
    getting_there: optText(80).describe('From the traveller’s home or current stop, e.g. "2 h by train from Brussels".'),
    lat: lat.nullish(), lon: lon.nullish(),
  })).min(1).max(6),
});

export const comparisonBlock = z.object({
  type: z.literal('comparison'),
  title: text(120),
  options: z.array(z.object({
    name: text(80),
    subtitle: optText(120),
    price: optText(60).describe('As found, with currency and what it covers, e.g. "€210 / night".'),
    pros: z.array(text(120)).max(4).default([]),
    cons: z.array(text(120)).max(3).default([]),
    recommended: z.boolean().default(false),
    url: optUrl.describe('Only a page you found with web search.'),
  })).min(2).max(4),
  recommendation: optText(300),
});

export const placesBlock = z.object({
  type: z.literal('places'),
  title: optText(120),
  /** "google" when the places came from Google Places in this answer (attribution, no own map). */
  source: z.enum(['elio', 'google']).default('elio'),
  items: z.array(z.object({
    place_id: optText(300).describe('The place_id from search_places, when the place came from it.'),
    name: text(80),
    kind: optText(40).describe('e.g. "Indonesian restaurant", "Museum".'),
    area: optText(80).describe('Neighbourhood and city, used for the map link.'),
    why: text(240),
    price: optText(20).describe('e.g. "€€".'),
    hours: optText(80),
    lat: lat.nullish(), lon: lon.nullish(),
    url: optUrl.describe('Only a page you found with web search.'),
    rating: z.number().min(0).max(5).nullish(), rating_count: z.number().int().min(0).nullish(),
    open_now: z.boolean().nullish(), maps_url: optUrl,
  })).min(1).max(8),
});

export const routeBlock = z.object({
  type: z.literal('route'),
  title: optText(120),
  stops: z.array(z.object({
    name: text(60), lat, lon,
    dates: optText(40).describe('e.g. "24–27 Oct" or "3 nights".'),
    note: optText(120),
  })).min(2).max(12),
  legs: z.array(z.object({ mode: z.enum(LEG_MODES).default('train'), duration: optText(30) })).max(11).default([]),
});

/** Live train board or connections. Filled by the server from the operator's data, never by the model. */
export const trainsBlock = z.object({
  type: z.literal('trains'),
  kind: z.enum(['departures', 'connections']),
  source: z.enum(['irail', 'ns']),
  station: text(80), to: optText(80),
  fetched_at: z.string().max(40),
  rows: z.array(z.object({
    time: text(5), delay_min: z.number().int(), canceled: z.boolean(), train: z.string().max(30), platform: z.string().max(10).nullable(),
    platform_changed: z.boolean(), to: z.string().max(80),
    arrival_time: z.string().max(5).nullish(), arrival_delay_min: z.number().int().nullish(), duration_min: z.number().int().nullish(), changes: z.number().int().nullish(),
  })).max(12),
});

/** Journey options (public transport, worldwide). Filled by the server from Google Routes, never by the model. */
export const journeysBlock = z.object({
  type: z.literal('journeys'),
  source: z.literal('google'),
  from: text(200), to: text(200),
  /** False once stored: Google's details are shown live only. */
  live: z.boolean().default(true),
  options: z.array(z.object({
    depart: z.string().max(20).nullable(), arrive: z.string().max(20).nullable(), minutes: z.number().int(), changes: z.number().int(), fare: z.string().max(30).nullable(),
    legs: z.array(z.object({
      mode: z.string().max(20), line: z.string().max(60).nullable(), vehicle: z.string().max(40).nullable(), headsign: z.string().max(80).nullable(),
      agency: z.string().max(80).nullable(), from: z.string().max(120).nullable(), to: z.string().max(120).nullable(),
      depart: z.string().max(20).nullable(), arrive: z.string().max(20).nullable(), stops: z.number().int().nullable(), minutes: z.number().int(),
    })).max(12),
  })).max(4),
});

/** What Elio may send with the show tool. */
export const modelBlockSchema = z.discriminatedUnion('type', [destinationsBlock, comparisonBlock, placesBlock, routeBlock]);
/** Everything that can be displayed (includes server-made blocks). */
export const blockSchema = z.discriminatedUnion('type', [destinationsBlock, comparisonBlock, placesBlock, routeBlock, trainsBlock, journeysBlock]);
export const showToolSchema = z.object({ blocks: z.array(modelBlockSchema).min(1).max(3) });

export type Block = z.output<typeof blockSchema>;
export type DestinationsBlock = z.output<typeof destinationsBlock>;
export type ComparisonBlock = z.output<typeof comparisonBlock>;
export type PlacesBlock = z.output<typeof placesBlock>;
export type RouteBlock = z.output<typeof routeBlock>;
export type TrainsBlock = z.output<typeof trainsBlock>;
export type JourneysBlock = z.output<typeof journeysBlock>;

const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return null; } };

/** Links are kept only when they point to a site Elio actually cited in this answer, so a block never carries an invented link. */
export function keepCitedLinks(blocks: Block[], citationUrls: string[]): Block[] {
  const cited = new Set(citationUrls.map(host).filter(Boolean));
  const ok = (u: string | null) => (u && cited.has(host(u)) ? u : null);
  return blocks.map((b) => {
    if (b.type === 'comparison') return { ...b, options: b.options.map((o) => ({ ...o, url: ok(o.url) })) };
    if (b.type === 'places') return { ...b, items: b.items.map((p) => ({ ...p, url: ok(p.url) })) };
    return b;
  });
}

/** Map links built by the app (never by the model). */
export function mapsSearchLink(...parts: (string | null | undefined)[]): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.filter(Boolean).join(', '))}`;
}
export function mapsRouteLink(stops: { name: string }[], mode: string): string {
  const [first, ...rest] = stops; const last = rest.pop();
  const travel = mode === 'car' ? 'driving' : mode === 'walk' ? 'walking' : mode === 'bike' ? 'bicycling' : 'transit';
  const q = new URLSearchParams({ api: '1', origin: first!.name, destination: (last ?? first)!.name, travelmode: travel });
  if (rest.length) q.set('waypoints', rest.map((s) => s.name).join('|'));
  return `https://www.google.com/maps/dir/?${q.toString()}`;
}
